import { useState } from 'react';
import { scanAllPages, type PagesResult } from '../api';

/**
 * Readiness for every page that feeds the brand analysis, weakest first (#4).
 *
 * The single-URL score (D-01) is genuinely misleading on its own: a homepage
 * routinely scores near-perfect while the deep service pages — the ones AI would
 * actually cite for a buyer question — are the thin ones. Those pages are
 * already crawled to build the questions, so scoring them costs one more fetch
 * each and no AI tokens at all.
 */
export default function PagesPanel({ url }: { url: string }): JSX.Element {
  const [data, setData] = useState<PagesResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(): Promise<void> {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      setData(await scanAllPages(url));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'page check failed');
    } finally {
      setLoading(false);
    }
  }

  const chip = (ok: boolean, label: string): JSX.Element => (
    <span className={`font-mono text-[11px] border rounded px-1.5 py-0.5 ${ok ? 'text-success border-success/40' : 'text-text-secondary border-warm-dark-2'}`}>
      {ok ? '✓' : '·'} {label}
    </span>
  );

  return (
    <div className="w-full border border-warm-dark-2 bg-warm-dark-2/20 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-text-primary">Check every page, not just this one</p>
          <p className="text-sm text-text-secondary mt-1">
            Your homepage can score perfectly while the pages AI would actually quote, the specific service pages, are
            the weak ones. This scores all of them, weakest first. No AI tokens, just a fetch each.
          </p>
        </div>
        <button
          onClick={() => void run()}
          disabled={loading}
          className="shrink-0 font-mono text-sm text-cyan border border-cyan/40 rounded-lg px-4 py-2 hover:bg-cyan/10 disabled:opacity-40 transition"
        >
          {loading ? 'checking…' : data ? 're-check' : 'Check all pages →'}
        </button>
      </div>

      {error && <p className="font-mono text-sm text-[#E0574A] mt-2">{error}</p>}

      {data && (
        <div className="mt-3">
          <p className="font-mono text-xs text-text-secondary mb-2">
            {data.pages.length} pages · weakest first · structure + entity signals only (the AI citability test is not
            run per page, because it is the expensive part and it is the same story across a site)
          </p>
          <div className="flex flex-col gap-1.5">
            {data.pages.map((p) => (
              <div key={p.url} className="border border-warm-dark-2 rounded-lg px-3 py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary truncate">{p.title}</p>
                    <a href={p.url} target="_blank" rel="noreferrer" className="font-mono text-xs text-text-secondary hover:text-cyan truncate block">
                      {p.url.replace(/^https?:\/\/[^/]+/, '') || '/'}{p.isEntry && <span className="text-cyan/70"> · the page you scanned</span>}
                    </a>
                  </div>
                  <span className={`font-mono shrink-0 ${p.score >= 90 ? 'text-success' : p.score >= 70 ? 'text-estimated' : 'text-[#E0574A]'}`}>
                    {p.score}<span className="text-text-secondary/60">/100</span>
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {chip(p.h1Count === 1, 'single h1')}
                  {chip(p.hasFaq, 'FAQ')}
                  {chip(p.hasTables, 'table')}
                  {chip(p.hasLists, 'lists')}
                  {chip(Boolean(p.modifiedDate), 'updated date')}
                  {chip(p.numberDensityPer1k >= 10, 'concrete numbers')}
                  <span className="font-mono text-[11px] text-text-secondary/60 px-1">{p.wordCount}w</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
